import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, User, ArrowUpRight } from "lucide-react";
import { BLOG, COMPANY } from "@/lib/data";
import { useDemoModal } from "@/components/DemoModalContext";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setOpen } = useDemoModal();
  const post = BLOG.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, [slug]);

  const others = BLOG.filter((b) => b.slug !== slug).slice(0, 2);

  if (!post) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <p className="overline">404</p>
        <h1 className="heading-lg mt-4">Yazı bulunamadı</h1>
        <button onClick={() => navigate("/")} className="btn-primary mt-8" data-testid="blogpost-home">
          Ana Sayfaya Dön
        </button>
      </main>
    );
  }

  return (
    <main data-testid="blog-post">
      {/* Hero header */}
      <header className="relative h-[62vh] min-h-[440px] w-full overflow-hidden bg-ink">
        <img src={post.image} alt={post.title} className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-shell w-full pb-14">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to="/#blog"
                className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-white/80 transition-colors hover:text-white"
                data-testid="blogpost-back"
              >
                <ArrowLeft className="h-4 w-4" /> Blog'a Dön
              </Link>
              <span className="block w-fit rounded-full bg-orange px-3 py-1 text-xs font-bold text-white">
                {post.category}
              </span>
              <h1 className="mt-5 max-w-4xl text-3xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
                {post.title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
                <span className="inline-flex items-center gap-2">
                  <User className="h-4 w-4" /> {post.author}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Calendar className="h-4 w-4" /> {post.date}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4" /> {post.readTime}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Body */}
      <article className="container-shell py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <p className="text-xl font-medium leading-relaxed text-ink/80">{post.excerpt}</p>
          <div className="mt-10 space-y-6">
            {post.content.map((block, i) => {
              if (block.type === "h")
                return (
                  <h2 key={i} className="pt-4 text-2xl font-bold tracking-tight text-ink">
                    {block.text}
                  </h2>
                );
              if (block.type === "quote")
                return (
                  <blockquote
                    key={i}
                    className="border-l-4 border-orange bg-cloud py-5 pl-6 pr-4 text-lg font-medium italic leading-relaxed text-ink"
                  >
                    {block.text}
                  </blockquote>
                );
              return (
                <p key={i} className="text-base leading-[1.9] text-ink/70">
                  {block.text}
                </p>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-14 rounded-3xl bg-ink px-8 py-10 text-center">
            <h3 className="text-2xl font-extrabold tracking-tight text-white">
              Bu çözümü işletmenizde konuşalım.
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm text-white/60">
              Ücretsiz demo talebi oluşturun, uzman ekibimiz size özel çözümü sunsun.
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <button onClick={() => setOpen(true)} className="btn-primary" data-testid="blogpost-demo">
                Demo Talebi Oluştur
              </button>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-3.5 font-semibold text-white transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-white/15"
              >
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Other posts */}
      {others.length > 0 && (
        <section className="container-shell pb-24">
          <div className="mx-auto max-w-5xl">
            <h2 className="heading-lg mb-10 text-2xl md:text-3xl">Diğer Yazılar</h2>
            <div className="grid grid-cols-1 gap-[32px] md:grid-cols-2">
              {others.map((b) => (
                <Link
                  key={b.slug}
                  to={`/blog/${b.slug}`}
                  className="group flex overflow-hidden rounded-2xl border border-ink/5 bg-white shadow-[0_2px_10px_rgba(32,32,32,0.04)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(32,32,32,0.10)]"
                  data-testid={`other-post-${b.slug}`}
                >
                  <div className="h-auto w-32 shrink-0 overflow-hidden">
                    <img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-col p-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-orange">{b.category}</span>
                    <h3 className="mt-2 text-base font-bold leading-snug tracking-tight text-ink">{b.title}</h3>
                    <span className="mt-auto pt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors group-hover:text-orange">
                      Oku <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
